import { Component, DestroyRef, inject, Input, OnInit, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { FgdsProductType, FullProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { OriginalRegard7000PlusConfiguration } from "@project/gas-warning-center-configuration/regard-7000-plus/original-regard-7000-plus-configuration";
import { BehaviorSubject, combineLatest, map, ReplaySubject, startWith, tap } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-dashboard-selector",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UiKitModule,
    FilterProductComponent,
    ProductCardWithQuantityComponent,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./dashboard-selector.component.html",
  styleUrl: "./dashboard-selector.component.scss",
  standalone: true,
})
export class DashboardSelectorComponent implements OnInit {
  private readonly productService = inject(ProductService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) project!: Project;

  @Input() set originalDashboards(originalDashboards: OriginalRegard7000PlusConfiguration["dashboards"] | undefined) {
    if (!originalDashboards) {
      return;
    }
    const available = originalDashboards.filter((d) => d.product.isAvailable);
    if (available.length) {
      this.dashboardsForm.patchValue({ dashboards: available });
    }
  }

  readonly dashboardSelect$ = output<
    {
      product: Product;
      quantity: number;
    }[]
  >();

  protected readonly filteredDashboards = signal<Product[]>([]);

  private dashboardsForm = this.formBuilder.group({
    dashboards: new FormControl<ProductWithQuantity[]>([]),
  });
  private readonly dashboards$ = new ReplaySubject<Product[]>(1);
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);

  constructor() {
    this.dashboardsForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((formValue) => formValue.dashboards ?? []),
        startWith(this.dashboardsForm.value.dashboards ?? []),
      )
      .pipe(
        tap((dashboards) => this.dashboardSelect$.emit(dashboards)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.collectDashboards();
    this.handleSearchValueChange();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected getSelectedQuantity(product: Product) {
    const formValue = this.dashboardsForm.value.dashboards;
    if (!formValue) {
      return 0;
    }
    const selectedProduct = formValue.find((formProduct: ProductWithQuantity) => formProduct.product.id === product.id);
    return selectedProduct?.quantity ?? 0;
  }

  protected updateSelectedDashboards(quantity: number, product: Product) {
    const currentSelectedProducts = this.dashboardsForm.value.dashboards;
    if (!currentSelectedProducts) {
      return;
    }
    const updatedSelectedProducts = currentSelectedProducts.filter(
      (selectedProduct) => selectedProduct.product.id !== product.id,
    );
    if (quantity > 0) {
      updatedSelectedProducts.push({ product, quantity });
    }
    this.dashboardsForm.patchValue({ dashboards: updatedSelectedProducts });
  }

  private collectDashboards() {
    this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER)
      .pipe(
        take(1),
        map((products) => products.filter((p) => p.fullType === FullProductType.GASWARNINGCENTER_DB)),
      )
      .subscribe((dashboards) => {
        this.dashboards$.next(dashboards);
      });
  }

  private handleSearchValueChange() {
    combineLatest([this.searchValue$, this.dashboards$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchValue, dashboards]) => {
        if (!dashboards) {
          this.filteredDashboards.set([]);
          return;
        }
        if (!searchValue) {
          this.filteredDashboards.set(dashboards);
          return;
        }
        this.filteredDashboards.set(dashboards.filter((product) => this.productService.searchFilter(product, searchValue)));
      });
  }
}
